/**
 * Silent update policy.
 *
 * A new build (detected through `version.json`, a waiting service worker, or a
 * lazy chunk that no longer exists) is never announced. It is applied — by
 * reloading the page — at the first moment that cannot interrupt the user:
 *
 *  - the tab goes to the background, so the reload is invisible;
 *  - a client-side route change, where the user has already left the screen
 *    they were on and a full navigation in place of the SPA transition is all
 *    but indistinguishable.
 *
 * Both moments still yield when something on screen looks like work in
 * progress: an explicit hold (`useHoldAppUpdate`), an open dialog, a focused
 * text field, or a text input / textarea holding a value. A hidden-tab reload additionally waits until the user has
 * been idle for a while, because state changed through cards, sliders or
 * checkboxes leaves no trace in the DOM heuristic — someone who just picked an
 * option and flipped to another tab is mid-task, not done. The update simply
 * waits for the next opportunity.
 *
 * The registration module signals a waiting worker through a window event, so
 * this module can import from it without a cycle.
 */
import {
  SW_UPDATE_EVENT,
  applyServiceWorkerUpdate,
} from './registerServiceWorker';

export type AppUpdateTrigger = 'detected' | 'hidden' | 'navigation';

/** sessionStorage key: the deployed version this tab last reloaded for. */
const APPLIED_VERSION_KEY = 'homepoint:applied-update-version';
/** sessionStorage key: when this tab last reloaded to apply an update. */
const APPLIED_AT_KEY = 'homepoint:applied-update-at';
/** A reload for an update is never repeated faster than this. */
const MIN_REAPPLY_MS = 60 * 1000;
/** How long the user must have been idle before a hidden tab may reload. */
export const IDLE_BEFORE_HIDDEN_RELOAD_MS = 2 * 60 * 1000;

/** Version label used when a waiting worker or missing chunk is the only signal. */
export const UNKNOWN_UPDATE_VERSION = 'new';

let pendingVersion: string | null = null;
let applying = false;
let listenersInstalled = false;
let lastInteractionAt = 0;
let hiddenRetryTimer: number | null = null;

const readSession = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* private mode or storage disabled — the guard degrades to the in-memory flags */
  }
};

/** True while an update has been detected and not yet applied. */
export const isAppUpdatePending = (): boolean => pendingVersion !== null;

/**
 * Selector for fields whose contents a reload would discard. Checkboxes,
 * radios and buttons carry no free text and are excluded.
 */
const TEXT_FIELD_SELECTOR = [
  'input:not([type=hidden]):not([type=checkbox]):not([type=radio])' +
    ':not([type=submit]):not([type=button]):not([type=reset]):not([type=file])',
  'textarea',
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(',');

const isEditableElement = (element: Element | null): boolean => {
  if (!element) return false;
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
  ) {
    return !element.readOnly && !element.disabled;
  }
  return (element as HTMLElement).isContentEditable === true;
};

const holdsText = (element: Element): boolean => {
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
  ) {
    if (element.readOnly || element.disabled) return false;
    return element.value.trim() !== '';
  }
  return (element.textContent ?? '').trim() !== '';
};

let activeHolds = 0;

/**
 * Explicit "do not reload" signal for state the DOM heuristic cannot see —
 * a wizard step kept in React state, a drag in progress, an upload. Returns
 * the release function; holds nest, and the update stays deferred until the
 * last one is released. Components should reach for `useHoldAppUpdate`.
 */
export const holdAppUpdate = (): (() => void) => {
  activeHolds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    activeHolds -= 1;
  };
};

/** True while at least one explicit hold is active. */
export const isAppUpdateHeld = (): boolean => activeHolds > 0;

/**
 * Heuristic for "the user is in the middle of something": an explicit hold,
 * an open dialog, a focused editable field, or any text field holding a
 * value. Conservative on purpose — a false positive only defers the update to
 * the next opportunity.
 */
export const hasWorkInProgress = (): boolean => {
  if (isAppUpdateHeld()) return true;
  if (typeof document === 'undefined') return false;

  if (document.querySelector('[role="dialog"]')) return true;
  if (isEditableElement(document.activeElement)) return true;

  const fields = document.querySelectorAll(TEXT_FIELD_SELECTOR);
  for (let i = 0; i < fields.length; i += 1) {
    if (holdsText(fields[i])) return true;
  }
  return false;
};

/**
 * Records that a newer build is deployed. Ignored when this tab already
 * reloaded for that very version and is still running the old code — a cache
 * layer is lagging, and reloading again would only loop.
 */
export const markAppUpdateAvailable = (
  version: string = UNKNOWN_UPDATE_VERSION
): void => {
  if (readSession(APPLIED_VERSION_KEY) === version) {
    console.info(
      `Update to ${version} was already applied this session; not reloading again.`
    );
    return;
  }
  pendingVersion = version;
};

const reloadedTooRecently = (): boolean => {
  const last = Number(readSession(APPLIED_AT_KEY));
  return (
    Number.isFinite(last) && last > 0 && Date.now() - last < MIN_REAPPLY_MS
  );
};

const clearHiddenRetry = (): void => {
  if (hiddenRetryTimer !== null) {
    window.clearTimeout(hiddenRetryTimer);
    hiddenRetryTimer = null;
  }
};

/**
 * A hidden tab whose user was active moments ago is re-checked once the idle
 * window has elapsed, as long as it is still hidden by then. Background timers
 * are throttled, so the retry may land late; it never lands early.
 */
const scheduleHiddenRetry = (delayMs: number): void => {
  clearHiddenRetry();
  hiddenRetryTimer = window.setTimeout(() => {
    hiddenRetryTimer = null;
    if (document.hidden) applyAppUpdateIfIdle('hidden');
  }, delayMs);
};

/**
 * Applies the pending update if this is a safe moment. `detected` only counts
 * as safe while the tab is already hidden; `hidden` and `navigation` are safe
 * unless work is in progress. A hidden tab also waits out the idle window,
 * re-checking on its own once it has passed.
 *
 * @returns whether a reload was started.
 */
export const applyAppUpdateIfIdle = (trigger: AppUpdateTrigger): boolean => {
  if (pendingVersion === null || applying) return false;
  if (trigger === 'detected' && !document.hidden) return false;
  if (hasWorkInProgress()) return false;
  if (reloadedTooRecently()) return false;

  if (trigger !== 'navigation') {
    const idleFor = Date.now() - lastInteractionAt;
    if (idleFor < IDLE_BEFORE_HIDDEN_RELOAD_MS) {
      scheduleHiddenRetry(IDLE_BEFORE_HIDDEN_RELOAD_MS - idleFor);
      return false;
    }
  }

  applying = true;
  writeSession(APPLIED_VERSION_KEY, pendingVersion);
  writeSession(APPLIED_AT_KEY, String(Date.now()));
  console.info(`Applying update to ${pendingVersion} (${trigger}).`);
  applyServiceWorkerUpdate();
  return true;
};

/**
 * Wires the window-level triggers. Idempotent; call once at boot, before the
 * service worker is registered so no update signal is missed.
 */
export const installAppUpdateListeners = (): void => {
  if (typeof window === 'undefined' || listenersInstalled) return;
  listenersInstalled = true;

  window.addEventListener(SW_UPDATE_EVENT, () => {
    markAppUpdateAvailable(UNKNOWN_UPDATE_VERSION);
    applyAppUpdateIfIdle('detected');
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      applyAppUpdateIfIdle('hidden');
    } else {
      clearHiddenRetry();
    }
  });

  const noteInteraction = () => {
    lastInteractionAt = Date.now();
  };
  const interactionOptions: AddEventListenerOptions = {
    capture: true,
    passive: true,
  };
  window.addEventListener('pointerdown', noteInteraction, interactionOptions);
  window.addEventListener('keydown', noteInteraction, interactionOptions);
  window.addEventListener('change', noteInteraction, interactionOptions);
};

/** Test-only: resets module state between cases. */
export const resetAppUpdateStateForTests = (): void => {
  clearHiddenRetry();
  pendingVersion = null;
  applying = false;
  listenersInstalled = false;
  lastInteractionAt = 0;
  activeHolds = 0;
};
